import type { SyntaxNode } from 'web-tree-sitter';
import type { SymbolTag } from './types.js';
import type { ScopeAnchor } from './scope-chain.js';
import { serializeScopeChain } from './scope-chain.js';
import { createCodeNavYieldState, yieldDuringCodeNavWork } from './yield-during-work.js';

const SCOPE_CONTAINER_TYPES = new Set([
  'function_declaration',
  'function_expression',
  'generator_function',
  'generator_function_declaration',
  'method_definition',
  'method_signature',
  'abstract_method_signature',
  'class_declaration',
  'abstract_class_declaration',
  'interface_declaration',
  'module',
  'arrow_function',
  'function_type',
  'function_item',
  'impl_item',
  'function_definition',
  'class_definition',
]);

function scopeAnchorFromNode(node: SyntaxNode): ScopeAnchor {
  const nameNode = node.childForFieldName('name');
  if (nameNode) {
    return {
      startLine: nameNode.startPosition.row,
      startColumn: nameNode.startPosition.column,
    };
  }
  return {
    startLine: node.startPosition.row,
    startColumn: node.startPosition.column,
  };
}

export function findEnclosingScopeChain(nameNode: SyntaxNode): ScopeAnchor[] {
  const chain: ScopeAnchor[] = [];
  let current: SyntaxNode | null = nameNode.parent;

  while (current) {
    if (SCOPE_CONTAINER_TYPES.has(current.type)) {
      chain.push(scopeAnchorFromNode(current));
    }
    current = current.parent;
  }

  return chain;
}

export function findEnclosingScopes(nameNode: SyntaxNode): {
  scope: ScopeAnchor | null;
  parentScope: ScopeAnchor | null;
  scopeChain: ScopeAnchor[];
} {
  const scopeChain = findEnclosingScopeChain(nameNode);
  return {
    scope: scopeChain[0] ?? null,
    parentScope: scopeChain[1] ?? null,
    scopeChain,
  };
}

export async function enrichTagsWithEnclosingScope(
  tags: SymbolTag[],
  nameNodes: Map<string, SyntaxNode>,
): Promise<SymbolTag[]> {
  const state = createCodeNavYieldState();
  const out: SymbolTag[] = [];
  for (const tag of tags) {
    await yieldDuringCodeNavWork(state);
    const key = `${tag.startLine}:${tag.startColumn}:${tag.name}:${tag.role}:${tag.kind}`;
    const nameNode = nameNodes.get(key);
    if (!nameNode) {
      out.push(tag);
      continue;
    }
    const { scope, parentScope, scopeChain } = findEnclosingScopes(nameNode);
    if (!scope) {
      out.push(tag);
      continue;
    }
    out.push({
      ...tag,
      scopeStartLine: scope.startLine,
      scopeStartColumn: scope.startColumn,
      parentScopeStartLine: parentScope?.startLine,
      parentScopeStartColumn: parentScope?.startColumn,
      scopeChain: serializeScopeChain(scopeChain) ?? undefined,
    });
  }
  return out;
}

export function scopeKey(scope: ScopeAnchor | null | undefined): string | null {
  if (scope == null) return null;
  return `${scope.startLine}:${scope.startColumn}`;
}

export function scopesMatch(
  aLine: number | null | undefined,
  aCol: number | null | undefined,
  bLine: number | null | undefined,
  bCol: number | null | undefined,
): boolean {
  return aLine != null && aCol != null && bLine != null && bCol != null && aLine === bLine && aCol === bCol;
}
